// src/pages/Products/ProductModals.jsx
import React from "react";
import Card from "../../../components/ui/Card.jsx";
import {
  MortgageProductTypeBadge,
  SectorBadge,
} from "../../../components/ui/TypeBadge.jsx";
import { X } from "lucide-react";

// VALUES must match Frappe DocType options exactly
const FEE_TYPES = [
  { key: "POUND", label: "£ Flat", value: "Pound" },
  { key: "PERCENT", label: "% Loan", value: "Percentage" },
  { key: "MIN", label: "Min £", value: "Mini Fee" },
];

const formatScheduledDateTime = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  return raw.replace(/(\d{2}:\d{2}):\d{2}$/, "$1");
};

const toDateInputValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const d = raw.split(" ")[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : "";
};

/** Parse API `date_time` (YYYY-MM-DD HH:mm:ss, DD-MM-YYYY HH:mm:ss, or ISO) → `datetime-local` value */
function scheduleRawToDatetimeLocal(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${y}-${mo}-${da}T${h}:${mi}`;
  }
  if (/^\d{2}-\d{2}-\d{4}/.test(s)) {
    const [datePart, timePart = "00:00:00"] = s.split(/\s+/);
    const [dd, mm, yyyy] = datePart.split("-");
    if (!yyyy || !mm || !dd) return "";
    const [hh = "00", mi = "00"] = (timePart || "").split(":");
    return `${yyyy}-${mm}-${dd}T${String(hh).padStart(2, "0")}:${String(mi).padStart(2, "0")}`;
  }
  const d = new Date(s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${mo}-${da}T${h}:${mi}`;
}

/**
 * HTML datetime-local ("YYYY-MM-DDTHH:MM") → Frappe "YYYY-MM-DD HH:MM:SS"
 * (Same convention as lender manual product entry.)
 */
export function scheduleDatetimeLocalToFrappe(dtLocal) {
  if (!dtLocal) return "";
  const [date, timeRaw] = String(dtLocal).trim().split("T");
  if (!date) return "";
  const time =
    (timeRaw || "00:00").length === 5 ? `${timeRaw}:00` : timeRaw || "00:00:00";
  return `${date} ${time}`;
}

function ModalShell({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-3xl mx-4">
        <Card className="relative p-6">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-semibold mb-4">{title}</h2>
          {children}
        </Card>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* VIEW MODAL                                                          */
/* ------------------------------------------------------------------ */

function ViewInvoiceProductBody({ item }) {
  const scheduleFlagRaw = item.product_schedule ?? item._raw?.product_schedule ?? false;
  const isScheduled =
    String(item.status || "").trim().toLowerCase() === "scheduled" ||
    scheduleFlagRaw === true ||
    String(scheduleFlagRaw).toLowerCase() === "true" ||
    String(scheduleFlagRaw) === "1";
  const scheduledDateTime = formatScheduledDateTime(
    item.date_time || item._raw?.date_time
  );
  const lenderCode =
    item._id ||
    item._raw?.name ||
    item.lender_product_code ||
    item._raw?.lender_product_code ||
    "—";

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
      <div>
        <div className="text-xs text-gray-500 mb-1">Sector</div>
        <div className="font-medium text-gray-900">
          <SectorBadge sector={item.sector || item.product} />
        </div>
        <div className="text-xs text-sky-600 mt-0.5 font-medium">{lenderCode}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">IP</div>
        <div className="font-medium text-gray-900">{item.ip || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Service fee</div>
        <div className="font-medium text-gray-900">{item.serviceFee || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Mini service fee</div>
        <div className="font-medium text-gray-900">{item.miniServiceFee || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Discount rate</div>
        <div className="font-medium text-gray-900">{item.discountRate || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Contract Term (months)</div>
        <div className="font-medium text-gray-900">
          {item.contractTerm ?? item.term ?? "—"}
        </div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Amount range</div>
        <div className="font-medium text-gray-900">{item.range || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Status</div>
        <div className="font-medium text-gray-900">{item.status || "—"}</div>
      </div>
      {isScheduled && (
        <div className="sm:col-span-2">
          <div className="text-xs text-gray-500 mb-1">Scheduled date &amp; time</div>
          <div className="font-medium text-amber-700">{scheduledDateTime}</div>
        </div>
      )}
    </div>
  );
}

export function ViewProductModal({ item, productLine = "btl", onClose }) {
  if (!item) return null;

  if (productLine === "if") {
    return (
      <ModalShell title="Product Details" onClose={onClose}>
        <ViewInvoiceProductBody item={item} />
      </ModalShell>
    );
  }

  const productType =
    item.type || item._raw?.product_type || item.ifCategory || "—";
  const expiryDateRaw =
    item.expiryDate ||
    item.expiry_date ||
    item.product_expiry_date ||
    item.product_expiray_date ||
    item._raw?.expiry_date ||
    item._raw?.product_expiry_date ||
    item._raw?.product_expiray_date ||
    "";
  const expiryDate = String(expiryDateRaw || "").trim() || "—";
  const scheduleFlagRaw =
    item.product_schedule ??
    item._raw?.product_schedule ??
    false;
  const isScheduled =
    String(item.status || "").trim().toLowerCase() === "scheduled" ||
    scheduleFlagRaw === true ||
    String(scheduleFlagRaw).toLowerCase() === "true" ||
    String(scheduleFlagRaw) === "1";
  const scheduledDateTime = formatScheduledDateTime(
    item.date_time || item._raw?.date_time
  );

  return (
    <ModalShell title="Product Details" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Product</div>
            <div className="font-medium text-gray-900">{item.product}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Type</div>
            <div className="mt-0.5">
              <MortgageProductTypeBadge type={productType} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Rate</div>
            <div className="font-medium">{item.rate}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Arrangement Fee</div>
            <div className="font-medium">{item.arrangementFee}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Term</div>
            <div className="font-medium">{item.term}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Amount Range</div>
            <div className="font-medium">{item.range}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Status</div>
            <div className="font-medium">{item.status}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Product Fee Type</div>
            <div className="font-medium">
              {item.feeType ||
                item.productFeeType ||
                item._raw?.product_fee_type ||
                "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Expiry Date</div>
            <div className="font-semibold text-rose-600">{expiryDate}</div>
          </div>
          {isScheduled && (
            <>
              <div>
                <div className="text-xs text-gray-500">Product Scheduled</div>
                <div className="font-medium text-amber-700">Yes</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Scheduled Date &amp; Time</div>
                <div className="font-medium text-amber-700">{scheduledDateTime}</div>
              </div>
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

/* ------------------------------------------------------------------ */
/* EDIT MODAL                                                          */
/* ------------------------------------------------------------------ */

function EditInvoiceProductForm({ item, onSave, onClose }) {
  const safeItem = item || {};
  const [form, setForm] = React.useState({
    sector: safeItem.sector || safeItem.product || "",
    ip: String(
      safeItem._raw?.ip_percent ??
        safeItem._ipNum ??
        safeItem.ip ??
        ""
    ).replace(/%/g, ""),
    serviceFee: String(
      safeItem._raw?.service_fee_percent ??
        safeItem._serviceFeeNum ??
        safeItem.serviceFee ??
        ""
    ).replace(/%/g, ""),
    miniServiceFee: String(
      safeItem._raw?.min_service_fee ??
        safeItem._miniServiceFeeNum ??
        safeItem.miniServiceFee ??
        ""
    ).replace(/[£%,\s]/g, ""),
    discountRate: String(
      safeItem._raw?.discount_rate_percent ??
        safeItem._raw?.discount_rate ??
        safeItem._discountRateNum ??
        safeItem.discountRate ??
        ""
    ).replace(/%/g, ""),
    term: String(
      safeItem._raw?.contract_term_months ??
        safeItem._termMonths ??
        safeItem.contractTerm ??
        safeItem.term ??
        ""
    ).replace(/[^\d]/g, ""),
    range: safeItem.range || "",
    status: safeItem.status || "Active",
    scheduleAt: scheduleRawToDatetimeLocal(
      safeItem.date_time || safeItem._raw?.date_time || ""
    ),
  });

  if (!item) return null;

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    const st = String(form.status || "").trim().toLowerCase();
    if (st === "scheduled" && !String(form.scheduleAt || "").trim()) {
      window.alert("Please select a go-live date and time for the scheduled product.");
      return;
    }
    onSave({
      ...item,
      ...form,
      sector: form.sector,
      ip: form.ip,
      category: "IF",
    });
  }

  const isScheduling = String(form.status || "").trim().toLowerCase() === "scheduled";

  return (
    <ModalShell title="Edit Product" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Sector</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.sector}
              onChange={(e) => updateField("sector", e.target.value)}
            />
          </label>
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">IP (%)</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.ip}
              onChange={(e) => updateField("ip", e.target.value)}
              placeholder="e.g. 70"
            />
          </label>
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Service fee (%)</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.serviceFee}
              onChange={(e) => updateField("serviceFee", e.target.value)}
            />
          </label>
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Mini service fee (%)</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.miniServiceFee}
              onChange={(e) => updateField("miniServiceFee", e.target.value)}
            />
          </label>
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Discount rate (%)</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.discountRate}
              onChange={(e) => updateField("discountRate", e.target.value)}
            />
          </label>
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Contract Term (months)</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.term}
              onChange={(e) => updateField("term", e.target.value)}
              placeholder="e.g. 24"
            />
          </label>
          <label className="block md:col-span-2">
            <div className="text-xs text-gray-500 mb-1">Amount range</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.range}
              onChange={(e) => updateField("range", e.target.value)}
              placeholder="e.g. £50k - £500k"
            />
          </label>
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Status</div>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) => updateField("status", e.target.value)}
            >
              <option>Active</option>
              <option>Deactivated</option>
              <option>Scheduled</option>
            </select>
          </label>
          {isScheduling && (
            <label className="block">
              <div className="text-xs text-gray-500 mb-1">Go-live date &amp; time</div>
              <input
                type="datetime-local"
                className="w-full border rounded-lg px-3 py-2"
                value={form.scheduleAt}
                onChange={(e) => updateField("scheduleAt", e.target.value)}
              />
            </label>
          )}
        </div>
        <div className="pt-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-semibold"
          >
            Save Changes
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function EditBtlProductForm({ item, onSave, onClose }) {
  // parent only renders when item exists, but use a safe fallback for hooks
  const safeItem = item || {};

  const initialFeeType =
    safeItem.feeType ||
    safeItem.productFeeType ||
    safeItem._raw?.product_fee_type ||
    FEE_TYPES[0].value;

  const [form, setForm] = React.useState({
    product: safeItem.product || "",
    category:
      safeItem.category ||
      safeItem._raw?.product_category ||
      safeItem._raw?.category ||
      "",
    type:
      safeItem.type ||
      safeItem.originalType ||
      safeItem._raw?.product_type ||
      "",
    rate: safeItem.rate || "",
    arrangementFee: safeItem.arrangementFee || "",
    term: safeItem.term || "",
    range: safeItem.range || "",
    expiryDate: toDateInputValue(
      safeItem.expiryDate ||
        safeItem.expiry_date ||
        safeItem.product_expiry_date ||
        safeItem.product_expiray_date ||
        safeItem._raw?.expiry_date ||
        safeItem._raw?.product_expiry_date ||
        safeItem._raw?.product_expiray_date
    ),
    status: safeItem.status || "Active",
    feeType: initialFeeType,
    scheduleAt: scheduleRawToDatetimeLocal(
      safeItem.date_time || safeItem._raw?.date_time || ""
    ),
  });

  // derive symbol based on selected fee type (READ-ONLY)
  const feeSymbol =
    form.feeType === "Percentage"
      ? "%"
      : "£"; // Pound & Mini Fee both use £ symbol

  // After hooks: if somehow no item, render nothing (keeps ESLint happy)
  if (!item) return null;

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    const st = String(form.status || "").trim().toLowerCase();
    if (st === "scheduled" && !String(form.scheduleAt || "").trim()) {
      window.alert("Please select a go-live date and time for the scheduled product.");
      return;
    }
    onSave({
      ...item,
      ...form,
    });
  }

  const isScheduling = String(form.status || "").trim().toLowerCase() === "scheduled";

  const activeKey =
    FEE_TYPES.find((t) => t.value === form.feeType)?.key || "POUND";

  return (
    <ModalShell title="Edit Product" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Product</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.product}
              onChange={(e) => updateField("product", e.target.value)}
            />
          </label>

          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Type</div>
            <select
              className="w-full border rounded-lg px-3 py-2 bg-white"
              value={form.type}
              onChange={(e) => updateField("type", e.target.value)}
            >
              <option value="">—</option>
              <option value="Fixed">Fixed</option>
              <option value="Variable">Variable</option>
              <option value="Tracker">Tracker</option>
              <option value="Discounted">Discounted</option>
            </select>
          </label>

          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Rate</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.rate}
              onChange={(e) => updateField("rate", e.target.value)}
            />
          </label>

          {/* ARRANGEMENT FEE WITH READONLY SYMBOL + INLINE OPTIONS */}
          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Arrangement Fee</div>
            <div className="relative">
              {/* READ-ONLY SYMBOL */}
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500 text-sm">
                {feeSymbol}
              </span>

              {/* USER TYPES ONLY NUMBER */}
              <input
                className="w-full border rounded-lg pl-7 pr-32 py-2"
                value={form.arrangementFee}
                onChange={(e) => updateField("arrangementFee", e.target.value)}
                placeholder="e.g. 999 or 1.5"
              />

              {/* THREE OPTIONS ON RIGHT, INSIDE FIELD */}
              <div className="absolute inset-y-0 right-1 flex items-center gap-1">
                {FEE_TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => updateField("feeType", t.value)}
                    className={`px-2 py-0.5 rounded-full border text-[10px] font-medium whitespace-nowrap ${
                      activeKey === t.key
                        ? "bg-sky-600 text-white border-sky-600"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </label>

          <label className="block">
            <div className="text-xs text-gray-500 mb-1">Term (years)</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.term}
              onChange={(e) => updateField("term", e.target.value)}
            />
          </label>

          <label className="block md:col-span-2">
            <div className="text-xs text-gray-500 mb-1">Amount Range</div>
            <input
              className="w-full border rounded-lg px-3 py-2"
              value={form.range}
              onChange={(e) => updateField("range", e.target.value)}
              placeholder="e.g. £50k - £500k"
            />
          </label>

          <label className="block md:col-span-2 md:w-1/3">
            <div className="text-xs text-gray-500 mb-1">Product Expiry Date</div>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2"
              value={form.expiryDate}
              onChange={(e) => updateField("expiryDate", e.target.value)}
            />
          </label>

          <label className="block md:col-span-2 md:w-1/3">
            <div className="text-xs text-gray-500 mb-1">Status</div>
            <select
              className="w-full border rounded-lg px-3 py-2"
              value={form.status}
              onChange={(e) => updateField("status", e.target.value)}
            >
              <option>Active</option>
              <option>Deactivated</option>
              <option>Scheduled</option>
            </select>
          </label>

          {isScheduling && (
            <label className="block md:col-span-2">
              <div className="text-xs text-gray-500 mb-1">Go-live date &amp; time</div>
              <input
                type="datetime-local"
                className="w-full border rounded-lg px-3 py-2"
                value={form.scheduleAt}
                onChange={(e) => updateField("scheduleAt", e.target.value)}
              />
            </label>
          )}
        </div>

        <div className="pt-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700 text-sm font-semibold"
          >
            Save Changes
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

export function EditProductModal({ item, productLine = "btl", onSave, onClose }) {
  if (!item) return null;
  if (productLine === "if") {
    return <EditInvoiceProductForm item={item} onSave={onSave} onClose={onClose} />;
  }
  return <EditBtlProductForm item={item} onSave={onSave} onClose={onClose} />;
}

